import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import JSZip from "jszip";
import api from "../../api";
import "../../CSS/DashboardReport.css";
import useDraggableScroll from "../../hooks/useDraggableScroll";

/* =========================================================================
   Saami Tradestar Logistics — ILMS
   Dashboard Report  ·  React component with Real API Integration

   - Connected to backend DashboardReportController (/api/DashboardReport)
   - Live filtering, sortable columns, row selection
   - Real status logic (Delivered, Attempted, Returned, Out for Delivery, In Transit, Undelivered)
   - Export to Excel (CSV) + POD / POD 2 download as pod-<date>.zip of images
   ========================================================================= */

/* ---------------- helpers ---------------- */
const uniq = (arr) => [...new Set((arr || []).filter(Boolean))].sort();
const pad2 = (n) => String(n).padStart(2, "0");
const dateStamp = () => { const d = new Date(); return d.getFullYear() + "-" + pad2(d.getMonth() + 1) + "-" + pad2(d.getDate()); };
const fmtDate = (d) => {
    if (!d) return "—";
    const parts = d.split("-");
    if (parts.length === 3) return parts[2] + "-" + parts[1] + "-" + parts[0];
    return d;
};

const statusCls = (s) => ({
    DELIVERED: "dbdr-s-delivered",
    "IN TRANSIT": "dbdr-s-transit",
    "OUT FOR DELIVERY": "dbdr-s-out",
    UNDELIVERED: "dbdr-s-pending",
    PENDING: "dbdr-s-pending",
    ATTEMPTED: "dbdr-s-attempted",
    RETURNED: "dbdr-s-returned"
}[s] || "dbdr-s-pending");

function toCSV(rowsArr) {
    return rowsArr.map((r) => r.map((v) => { v = String(v == null ? "" : v); return /[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v; }).join(",")).join("\n");
}
function downloadBlob(name, blob) {
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
}

/* POD files are static files saved on the backend:
   POD   -> consignment_master.pod_name  -> /POD/{name}
   POD 2 -> consignment_master.Scan_name -> /POD/scan/{name}
   The controller returns these as podUrl / pod2Url. */
async function podImageBlob(r, which) {
    const rel = which === 2 ? r.pod2Url : r.podUrl;
    if (!rel) return null;
    try {
        // Plain GET (no custom headers) so the manual CORS header in Program.cs is enough
        const base = (api.defaults.baseURL || "").replace(/\/$/, "");
        const resp = await fetch(base + rel);
        if (resp.ok) return await resp.blob();
    } catch (e) {
        console.warn("POD fetch failed", e);
    }
    return null; // file missing on server - caller handles it (no fake image)
}

/* ============================ Auto-commit ComboBox ============================ */
const ComboBox = React.memo(function ComboBox({ value, options, onChange, placeholder = "Select…", searchable = true }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [hi, setHi] = useState(0);
    const [pos, setPos] = useState({ left: 0, top: 0, width: 0 });
    const ctlRef = useRef(null);
    const panelRef = useRef(null);
    const inputRef = useRef(null);

    const selected = useMemo(() => options.find((o) => String(o.value) === String(value)), [options, value]);
    const filtered = useMemo(() => {
        const query = q.toLowerCase().trim();
        if (!query) return options;
        return options.filter((o) => o.label.toLowerCase().includes(query));
    }, [options, q]);

    // Cap display to max 100 items for ultra-fast DOM rendering
    const MAX_OPTS = 100;
    const displayOpts = useMemo(() => filtered.slice(0, MAX_OPTS), [filtered]);

    const place = useCallback(() => {
        const el = ctlRef.current; if (!el) return;
        const r = el.getBoundingClientRect();
        const ph = Math.min(280, 44 + Math.max(1, displayOpts.length) * 34 + 8);
        const below = window.innerHeight - r.bottom;
        const top = below < ph + 8 && r.top > ph ? r.top - ph - 4 : r.bottom + 4;
        setPos({ left: r.left, top, width: Math.max(r.width, 160) });
    }, [displayOpts.length]);

    const commit = (v) => { onChange(v); setOpen(false); setQ(""); };
    const close = () => {
        const t = q.trim().toLowerCase();
        if (t) { const m = options.find((o) => o.label.toLowerCase() === t); if (m) onChange(m.value); }
        setOpen(false); setQ("");
    };

    useEffect(() => {
        if (!open) return;
        place();
        const fi = displayOpts.findIndex((o) => String(o.value) === String(value));
        setHi(fi < 0 ? 0 : fi);
        const onDown = (e) => {
            if (ctlRef.current && ctlRef.current.contains(e.target)) return;
            if (panelRef.current && panelRef.current.contains(e.target)) return;
            close();
        };
        const onScroll = () => place();
        document.addEventListener("mousedown", onDown, true);
        window.addEventListener("scroll", onScroll, true);
        window.addEventListener("resize", place);
        const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 20);
        return () => {
            document.removeEventListener("mousedown", onDown, true);
            window.removeEventListener("scroll", onScroll, true);
            window.removeEventListener("resize", place);
            clearTimeout(t);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const onKey = (e) => {
        if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, displayOpts.length - 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
        else if (e.key === "Enter") { e.preventDefault(); const o = displayOpts[hi]; if (o) commit(o.value); }
        else if (e.key === "Escape") { e.preventDefault(); close(); }
    };

    return (
        <>
            <div ref={ctlRef} className="dbdr-combo" onClick={() => setOpen((o) => !o)}>
                <span className={"dbdr-combo-val" + (selected ? "" : " dbdr-ph")}>{selected ? selected.label : placeholder}</span>
                <svg className="dbdr-combo-caret" viewBox="0 0 11 7"><path d="M1 1l4.5 4.5L10 1" strokeWidth="1.6" fill="none" strokeLinecap="round" /></svg>
            </div>
            {open && typeof document !== "undefined" && createPortal(
                <div ref={panelRef} className="dbdr-combo-panel" style={{ left: pos.left, top: pos.top, width: pos.width }}>
                    {searchable && (
                        <div className="dbdr-combo-search">
                            <input ref={inputRef} value={q} placeholder="Type to filter…" autoComplete="off"
                                onChange={(e) => { setQ(e.target.value); setHi(0); }} onKeyDown={onKey} />
                        </div>
                    )}
                    <div className="dbdr-combo-list">
                        {displayOpts.length ? displayOpts.map((o, i) => (
                            <div key={String(o.value) + "-" + i}
                                className={"dbdr-combo-opt " + (String(o.value) === String(value) ? "dbdr-sel " : "") + (i === hi ? "dbdr-hi" : "")}
                                onMouseEnter={() => setHi(i)} onClick={() => commit(o.value)}>{o.label}</div>
                        )) : <div className="dbdr-combo-empty">No match</div>}
                        {filtered.length > MAX_OPTS && (
                            <div className="dbdr-combo-more">Showing {MAX_OPTS} of {filtered.length} matches. Type to refine…</div>
                        )}
                    </div>
                </div>, document.body
            )}
        </>
    );
});

const ICON = { ok: "✓", err: "✕", warn: "⚠", info: "ℹ" };

/* ticker item */
const Ti = ({ label, val, cls, arrow }) => (
    <span className="dbdr-ti">{label} <span className={cls || "dbdr-ti-tk"}>{arrow ? <span className="dbdr-ti-delta">{arrow} </span> : null}{val}</span></span>
);

/* Default 30-day date range helper */
const getInitialFromDate = () => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split("T")[0];
};
const getInitialToDate = () => {
    return new Date().toISOString().split("T")[0];
};

/* ============================ main component ============================ */
export default function DashboardReport() {
    const [branch, setBranch] = useState("");
    const [dest, setDest] = useState("");
    const [from, setFrom] = useState(getInitialFromDate);
    const [to, setTo] = useState(getInitialToDate);
    const [pod, setPod] = useState("");
    const [pod2, setPod2] = useState("");
    const [status, setStatus] = useState("");
    const [party, setParty] = useState("");
    const [job, setJob] = useState("");
    const [svc, setSvc] = useState("");

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterOptions, setFilterOptions] = useState({
        branches: [], destinations: [], parties: [], jobs: [], services: [],
        statuses: ["DELIVERED", "IN TRANSIT", "OUT FOR DELIVERY", "UNDELIVERED", "ATTEMPTED", "RETURNED"]
    });

    const [showFilters, setShowFilters] = useState(true);
    const [sortKey, setSortKey] = useState("no");
    const [sortDir, setSortDir] = useState(1);
    const [selected, setSelected] = useState(() => new Set());
    const [flash, setFlash] = useState(0);
    const [toasts, setToasts] = useState([]);

    // Pagination states
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);

    const tableRef = useDraggableScroll();

    const toast = (msg, type = "info") => {
        const id = Date.now() + Math.random();
        setToasts((t) => [...t, { id, msg, type }]);
        setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 2600);
    };

    // Load dynamic data from Backend Controller
    const fetchReport = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                branch, dest, fromDate: from, toDate: to, pod, pod2, status, party, job, svc
            };
            const response = await api.get("/api/DashboardReport", { params });
            if (response.data && response.data.success) {
                const fetchedRows = (response.data.rows || []).map((r, i) => ({
                    no: i + 1,
                    bookingDate: r.bookingDate,
                    cnr: r.cnr,
                    origin: r.origin,
                    dest: r.dest,
                    job: r.job,
                    party: r.party,
                    pkgs: r.pkgs,
                    weight: r.weight,
                    service: r.service,
                    status: r.status,
                    pod: r.pod,
                    pod2: r.pod2,
                    podUrl: r.podUrl,
                    pod2Url: r.pod2Url
                }));
                setRows(fetchedRows);
                if (response.data.filterOptions) {
                    setFilterOptions(response.data.filterOptions);
                }
            } else {
                toast(response.data?.message || "Failed to load report", "err");
            }
        } catch (error) {
            console.error("Dashboard report load error:", error);
            toast("Failed to connect to backend server", "err");
        } finally {
            setLoading(false);
        }
    }, [branch, dest, from, to, pod, pod2, status, party, job, svc]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const opt = (vals, allLabel) => [{ value: "", label: allLabel }].concat((vals || []).map((v) => ({ value: v, label: v })));
    
    const branchOpts = useMemo(() => {
        const list = filterOptions.branches.length ? filterOptions.branches : (rows.length < 2000 ? uniq(rows.map((r) => r.origin)) : []);
        return opt(list, "All");
    }, [filterOptions.branches, rows]);

    const destOpts = useMemo(() => {
        const list = filterOptions.destinations.length ? filterOptions.destinations : (rows.length < 2000 ? uniq(rows.map((r) => r.dest)) : []);
        return opt(list, "All");
    }, [filterOptions.destinations, rows]);

    const partyOpts = useMemo(() => {
        const list = filterOptions.parties.length ? filterOptions.parties : (rows.length < 2000 ? uniq(rows.map((r) => r.party)) : []);
        return opt(list, "-- Select Party --");
    }, [filterOptions.parties, rows]);

    const jobOpts = useMemo(() => {
        const list = filterOptions.jobs.length ? filterOptions.jobs : (rows.length < 2000 ? uniq(rows.map((r) => r.job)) : []);
        return opt(list, "-- Select Jobname --");
    }, [filterOptions.jobs, rows]);

    const podOpts = [{ value: "", label: "All" }, { value: "Y", label: "Available" }, { value: "N", label: "Not Available" }];
    const statusOpts = opt(filterOptions.statuses || ["DELIVERED", "IN TRANSIT", "OUT FOR DELIVERY", "UNDELIVERED", "ATTEMPTED", "RETURNED"], "All");
    const svcOpts = opt(filterOptions.services.length ? filterOptions.services : ["DOMESTIC", "NFO", "EXPRESS", "SURFACE"], "All");

    /* Local sorting */
    const view = useMemo(() => {
        let v = [...rows];
        const k = sortKey, d = sortDir;
        v.sort((a, b) => {
            let x = a[k], y = b[k];
            if (k === "pkgs" || k === "weight" || k === "no" || k === "cnr") { x = Number(x) || 0; y = Number(y) || 0; }
            else if (k === "pod" || k === "pod2") { x = x ? 1 : 0; y = y ? 1 : 0; }
            else { x = String(x || "").toLowerCase(); y = String(y || "").toLowerCase(); }
            return x < y ? -1 * d : x > y ? 1 * d : 0;
        });
        return v;
    }, [rows, sortKey, sortDir]);

    useEffect(() => { setSelected(new Set()); setFlash((f) => f + 1); setPage(1); }, [rows]);
    useEffect(() => { setFlash((f) => f + 1); setPage(1); }, [sortKey, sortDir]);

    /* Pagination slices */
    const totalPages = useMemo(() => pageSize === 0 ? 1 : Math.ceil(view.length / pageSize) || 1, [view.length, pageSize]);
    
    const pagedView = useMemo(() => {
        if (pageSize === 0) return view;
        const start = (page - 1) * pageSize;
        return view.slice(start, start + pageSize);
    }, [view, page, pageSize]);

    const kpi = useMemo(() => {
        let dl = 0, trn = 0, pn = 0, pk = 0, wt = 0, podPend = 0, tr = 0, ofd = 0, podA = 0;
        const t = view.length;
        for (let i = 0; i < t; i++) {
            const r = view[i];
            const s = r.status;
            if (s === "DELIVERED") dl++;
            else if (s === "IN TRANSIT") { trn++; tr++; }
            else if (s === "OUT FOR DELIVERY") { trn++; ofd++; }
            else if (s === "UNDELIVERED" || s === "ATTEMPTED") pn++;
            pk += (Number(r.pkgs) || 0);
            wt += (Number(r.weight) || 0);
            if (r.pod) podA++; else podPend++;
        }
        return {
            t, dl, trn, pn, pk,
            wt: Math.round(wt * 100) / 100,
            podPend, tr, ofd, podA,
            pct: t ? Math.round((dl / t) * 100) : 0
        };
    }, [view]);

    const sortBy = (k) => { if (sortKey === k) setSortDir((d) => -d); else { setSortKey(k); setSortDir(1); } };
    const resetFilters = () => {
        setBranch(""); setDest(""); setPod(""); setPod2(""); setStatus(""); setParty(""); setJob(""); setSvc("");
        setFrom(getInitialFromDate()); setTo(getInitialToDate());
        setPage(1);
        toast("Filters cleared", "info");
    };

    const toggleRow = (cnr) => setSelected((s) => { const n = new Set(s); n.has(cnr) ? n.delete(cnr) : n.add(cnr); return n; });
    const allSel = pagedView.length > 0 && pagedView.every((r) => selected.has(r.cnr));
    const toggleAll = () => setSelected((prev) => {
        const n = new Set(prev);
        if (allSel) {
            pagedView.forEach((r) => n.delete(r.cnr));
        } else {
            pagedView.forEach((r) => n.add(r.cnr));
        }
        return n;
    });

    /* downloads */
    function exportExcel() {
        if (!view.length) { toast("Nothing to export", "warn"); return; }
        const head = ["No", "Booking Date", "Consignment No", "Booking Destination", "Delivery Destination", "Job Name", "No_Of_Pkg", "Weight", "Services", "Delivery Status", "POD", "POD 2"];
        const body = view.map((r) => [r.no, fmtDate(r.bookingDate), r.cnr, r.origin, r.dest, r.job, r.pkgs, r.weight, r.service, r.status, r.pod ? "Available" : "Not Available", r.pod2 ? "Available" : "Not Available"]);
        downloadBlob("dashboard-report_" + from + "_to_" + to + ".csv", new Blob([toCSV([head].concat(body))], { type: "text/csv;charset=utf-8;" }));
        toast("Exported " + view.length + " rows to Excel (CSV)", "ok");
    }

    async function podDownload(which) {
        const key = which === 2 ? "pod2" : "pod", label = which === 2 ? "POD 2" : "POD";
        let set = view.filter((r) => r[key]);
        if (selected.size) set = set.filter((r) => selected.has(r.cnr));
        if (!set.length) { toast("No " + label + " available in " + (selected.size ? "selection" : "current view"), "warn"); return; }
        if (typeof JSZip === "undefined") { toast("ZIP library not loaded", "err"); return; }
        toast("Packing " + set.length + " " + label + " image" + (set.length === 1 ? "" : "s") + "…", "info");
        const zip = new JSZip(), folder = zip.folder(which === 2 ? "POD2" : "POD");
        let added = 0, missing = 0;
        for (const r of set) {
            const blob = await podImageBlob(r, which);
            if (!blob) { missing++; continue; }
            const t = blob.type || "";
            const ext = t.indexOf("png") > -1 ? "png" : t.indexOf("pdf") > -1 ? "pdf" : "jpg";
            folder.file("POD" + (which === 2 ? "2" : "") + "_" + r.cnr + "." + ext, blob);
            added++;
        }
        if (!added) { toast("No " + label + " files found on server", "err"); return; }
        const content = await zip.generateAsync({ type: "blob" });
        const name = (which === 2 ? "pod2" : "pod") + "-" + dateStamp() + ".zip";
        downloadBlob(name, content);
        toast("Downloaded " + name + "  —  " + added + " file" + (added === 1 ? "" : "s"), "ok");
        if (missing) toast(missing + " file(s) not found on server", "warn");
    }

    async function dlOne(cnr, which) {
        const r = rows.find((x) => x.cnr === cnr); if (!r) return;
        const blob = await podImageBlob(r, which);
        if (!blob) { toast("POD file not found on server", "err"); return; }
        const t = blob.type || "";
        const ext = t.indexOf("png") > -1 ? "png" : t.indexOf("pdf") > -1 ? "pdf" : "jpg";
        downloadBlob("POD" + (which === 2 ? "2" : "") + "_" + cnr + "." + ext, blob);
        toast((which === 2 ? "POD 2" : "POD") + " for CN " + cnr + " downloaded", "ok");
    }

    const tickerItems = (
        <>
            <Ti label="CONSIGN" val={kpi.t} /><Ti label="DELIVERED" val={kpi.dl} cls="dbdr-ti-up" arrow="▲" />
            <Ti label="IN TRANSIT" val={kpi.tr} cls="dbdr-ti-am" arrow="►" /><Ti label="OUT-FOR-DLV" val={kpi.ofd} cls="dbdr-ti-fl" arrow="►" />
            <Ti label="UNDELIVERED" val={kpi.pn} cls="dbdr-ti-dn" arrow="▼" />
            <Ti label="DELIVERY%" val={kpi.pct + "%"} cls={kpi.pct >= 70 ? "dbdr-ti-up" : "dbdr-ti-dn"} arrow={kpi.pct >= 70 ? "▲" : "▼"} />
            <Ti label="PACKAGES" val={kpi.pk} /><Ti label="WEIGHT" val={kpi.wt + "kg"} />
            <Ti label="POD-OK" val={kpi.podA} cls="dbdr-ti-fl" /><Ti label="POD-PENDING" val={kpi.podPend} cls={kpi.podPend ? "dbdr-ti-dn" : "dbdr-ti-up"} arrow={kpi.podPend ? "▼" : "▲"} />
        </>
    );

    const th = (k, label, cls) => (
        <th data-k={k} className={(cls || "") + (sortKey === k ? " dbdr-sorted" : "")} onClick={() => sortBy(k)}>
            {label} <span className="dbdr-ar">{sortKey === k ? (sortDir > 0 ? "▲" : "▼") : ""}</span>
        </th>
    );

    const today = new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "short", day: "2-digit" });

    const kpiCards = [
        ["", kpi.t, "Consignments"],
        ["dbdr-kpi-dl", kpi.dl, "Delivered"],
        ["dbdr-kpi-tr", kpi.trn, "In Transit"],
        ["dbdr-kpi-pn", kpi.pn, "Undelivered"],
        ["dbdr-kpi-pk", kpi.pk, "Packages"],
        ["dbdr-kpi-wt", kpi.wt + " kg", "Weight"],
        ["dbdr-kpi-pod", kpi.podPend, "POD Pending"],
    ];

    const startRow = pageSize === 0 ? (view.length ? 1 : 0) : Math.min((page - 1) * pageSize + 1, view.length);
    const endRow = pageSize === 0 ? view.length : Math.min(page * pageSize, view.length);

    return (
        <div className="dbdr-page">

            {/* Page Header */}
            <div className="dbdr-page-header">
                <div>
                    <div className="dbdr-page-title">Dashboard <span>Report</span></div>
                    <div className="dbdr-page-sub">{today} &nbsp;·&nbsp; Consignment tracking &amp; POD downloads</div>
                </div>
                <div className="dbdr-btn-group">
                    <button className="dbdr-btn dbdr-btn-outline" onClick={() => setShowFilters((f) => !f)}>🎛 Filters</button>
                    <button className="dbdr-btn dbdr-btn-outline" onClick={resetFilters}>⟲ Reset</button>
                    <button className="dbdr-btn dbdr-btn-outline" onClick={exportExcel}>⬇ Export to Excel</button>
                    <button className="dbdr-btn dbdr-btn-primary" onClick={fetchReport} disabled={loading}>
                        {loading ? "⏳ Loading…" : "🔍 Search"}
                    </button>
                </div>
            </div>

            {/* Filters */}
            {showFilters && (
                <div className="dbdr-filters">
                    <div className="dbdr-filter-grid">
                        <div className="dbdr-field"><label>Branch (Booking)</label><ComboBox value={branch} options={branchOpts} onChange={setBranch} placeholder="All" /></div>
                        <div className="dbdr-field"><label>Destination</label><ComboBox value={dest} options={destOpts} onChange={setDest} placeholder="All" /></div>
                        <div className="dbdr-field"><label>From Date</label><input type="date" className="dbdr-input" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
                        <div className="dbdr-field"><label>To Date</label><input type="date" className="dbdr-input" value={to} onChange={(e) => setTo(e.target.value)} /></div>
                        <div className="dbdr-field"><label>POD Upload</label><ComboBox value={pod} options={podOpts} onChange={setPod} placeholder="All" searchable={false} /></div>
                        <div className="dbdr-field"><label>POD Upload 2</label><ComboBox value={pod2} options={podOpts} onChange={setPod2} placeholder="All" searchable={false} /></div>
                        <div className="dbdr-field"><label>Status</label><ComboBox value={status} options={statusOpts} onChange={setStatus} placeholder="All" searchable={false} /></div>
                        <div className="dbdr-field"><label>Party</label><ComboBox value={party} options={partyOpts} onChange={setParty} placeholder="-- Select Party --" /></div>
                        <div className="dbdr-field"><label>Job Name</label><ComboBox value={job} options={jobOpts} onChange={setJob} placeholder="-- Select Jobname --" /></div>
                        <div className="dbdr-field"><label>Services</label><ComboBox value={svc} options={svcOpts} onChange={setSvc} placeholder="All" searchable={false} /></div>
                    </div>
                </div>
            )}

            {/* KPI Strip */}
            <div className="dbdr-kpis">
                {kpiCards.map(([cls, value, label]) => (
                    <div key={label} className={"dbdr-kpi " + cls}>
                        <div className="dbdr-kpi-v">{value}</div>
                        <div className="dbdr-kpi-l">{label}</div>
                    </div>
                ))}
            </div>

            {/* Results */}
            <div>
                <div className="dbdr-results">
                    <div className="dbdr-res-hd">
                        <span className="dbdr-ct">Showing <b>{view.length}</b> consignments{selected.size > 0 && <> &nbsp;·&nbsp; <b>{selected.size}</b> selected</>}</span>
                        <span className="dbdr-sp" />
                        <div className="dbdr-dl-acts">
                            <button className="dbdr-btn dbdr-btn-dark dbdr-btn-sm" onClick={() => podDownload(1)}>⬇ POD Download (.zip)</button>
                            <button className="dbdr-btn dbdr-btn-dark dbdr-btn-sm" onClick={() => podDownload(2)}>⬇ POD 2 Download (.zip)</button>
                        </div>
                    </div>

                    <div className="dbdr-ticker"><div className="dbdr-ticker-track">{tickerItems}{tickerItems}</div></div>

                    <div className="dbdr-tbl-scroll" ref={tableRef}>
                        <table className="dbdr-tbl">
                            <thead><tr>
                                <th className="dbdr-nosort dbdr-c" style={{ width: 34 }}><input type="checkbox" className="dbdr-chk" checked={allSel} onChange={toggleAll} /></th>
                                <th className="dbdr-nosort dbdr-c" style={{ width: 40 }}>No</th>
                                {th("bookingDate", "Booking Date")}{th("cnr", "Consignment No")}{th("origin", "Booking Dest.")}{th("dest", "Delivery Dest.")}
                                {th("job", "Job Name")}{th("pkgs", "No_Of_Pkg", "dbdr-r")}{th("weight", "Weight", "dbdr-r")}{th("service", "Services")}
                                {th("status", "Delivery Status")}{th("pod", "POD", "dbdr-c")}{th("pod2", "POD 2", "dbdr-c")}
                            </tr></thead>
                            <tbody key={flash} className="dbdr-flash">
                                {loading ? (
                                    <tr><td colSpan={13} className="dbdr-empty">Loading real data from server…</td></tr>
                                ) : pagedView.length ? pagedView.map((r) => (
                                    <tr key={r.cnr} className={selected.has(r.cnr) ? "dbdr-sel" : ""}>
                                        <td className="dbdr-c"><input type="checkbox" className="dbdr-chk" checked={selected.has(r.cnr)} onChange={() => toggleRow(r.cnr)} /></td>
                                        <td className="dbdr-num">{r.no}</td>
                                        <td>{fmtDate(r.bookingDate)}</td>
                                        <td className="dbdr-cnr">{r.cnr}</td>
                                        <td>{r.origin}</td>
                                        <td>{r.dest}</td>
                                        <td className="dbdr-job">{r.job}</td>
                                        <td className="dbdr-r dbdr-num">{r.pkgs}</td>
                                        <td className="dbdr-r dbdr-num">{r.weight}</td>
                                        <td><span className={"dbdr-svc dbdr-svc-" + (r.service || "").replace(/\s+/g, "_")}>{r.service}</span></td>
                                        <td><span className={"dbdr-pill " + statusCls(r.status)}>{r.status}</span></td>
                                        <td className="dbdr-c">{r.pod ? <button type="button" className="dbdr-pod-av" onClick={() => dlOne(r.cnr, 1)}>Available</button> : <span className="dbdr-pod-no">Not Available</span>}</td>
                                        <td className="dbdr-c">{r.pod2 ? <button type="button" className="dbdr-pod-av" onClick={() => dlOne(r.cnr, 2)}>Available</button> : <span className="dbdr-pod-no">Not Available</span>}</td>
                                    </tr>
                                )) : <tr><td colSpan={13} className="dbdr-empty">No consignments match the selected filters.</td></tr>}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Bar */}
                    <div className="dbdr-pg-bar">
                        <div className="dbdr-pg-info">
                            Showing <b>{startRow}</b> to <b>{endRow}</b> of <b>{view.length}</b> records
                        </div>
                        <div className="dbdr-pg-ctrls">
                            <label className="dbdr-pg-sz-lbl">
                                Rows per page:
                                <select className="dbdr-pg-select" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value={250}>250</option>
                                    <option value={500}>500</option>
                                    <option value={1000}>1000</option>
                                    <option value={0}>All ({view.length})</option>
                                </select>
                            </label>

                            <div className="dbdr-pg-btns">
                                <button className="dbdr-pg-btn" onClick={() => setPage(1)} disabled={page <= 1}>«</button>
                                <button className="dbdr-pg-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>‹ Prev</button>
                                <span className="dbdr-pg-curr">Page <b>{page}</b> of <b>{totalPages}</b></span>
                                <button className="dbdr-pg-btn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Next ›</button>
                                <button className="dbdr-pg-btn" onClick={() => setPage(totalPages)} disabled={page >= totalPages}>»</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Toasts */}
            <div className="dbdr-toast-wrap">
                {toasts.map((t) => (<div key={t.id} className={"dbdr-toast dbdr-toast-" + t.type}><span className="dbdr-toast-ico">{ICON[t.type] || ICON.info}</span>{t.msg}</div>))}
            </div>
        </div>
    );
}